import { StringDevUIFieldDescriptor as UIFieldDescriptor } from "../../core/ui/StringDevUISchema";
import { GridAdapter } from "./GridAdapter";

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * Dot Grid adapter.
 *
 * Places small dots or crosses at every intersection of an invisible grid.
 * Much less visual noise than full lines — ideal for containers with
 * dense content (text, images) where you still want alignment guides.
 */
export class DotGridAdapter extends GridAdapter {
  private static readonly MAX_DOT_CELLS = 120_000;
  private static readonly MAX_CROSS_CELLS = 80_000;

  readonly type = "dot-grid";
  readonly label = "Dots";
  readonly icon = `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="11" y="3" width="2" height="2" fill="currentColor"/><rect x="11" y="7" width="2" height="2" fill="currentColor"/><rect x="11" y="11" width="2" height="2" fill="currentColor"/><rect x="7" y="3" width="2" height="2" fill="currentColor"/><rect x="7" y="7" width="2" height="2" fill="currentColor"/><rect x="7" y="11" width="2" height="2" fill="currentColor"/><rect x="3" y="3" width="2" height="2" fill="currentColor"/><rect x="3" y="7" width="2" height="2" fill="currentColor"/><rect x="3" y="11" width="2" height="2" fill="currentColor"/></svg>`;

  private static readonly UNIT_OPTIONS = [
    { value: "px", label: "px" },
    { value: "%", label: "%" },
    { value: "vw", label: "vw" },
    { value: "vh", label: "vh" },
    { value: "em", label: "em" },
    { value: "rem", label: "rem" },
  ] as const;

  getDefaults(): Record<string, any> {
    return {
      step: 16,
      stepUnit: "px",
      dotSize: 1.5,
      shape: "dot",
      color: "#a0a0a0",
      opacity: 0.4,
    };
  }

  getUISchema(): UIFieldDescriptor[] {
    return [
      {
        type: "range",
        key: "step",
        label: "Step",
        default: 16,
        min: 0,
        max: 100,
        step: 1,
        units: DotGridAdapter.UNIT_OPTIONS,
        defaultUnit: "px",
      },
      { type: "range", key: "dotSize", label: "Size", default: 1.5, min: 0.5, max: 6, step: 0.5 },
      {
        type: "select",
        key: "shape",
        label: "Shape",
        default: "dot",
        options: [
          { value: "dot", label: "Dot ●" },
          { value: "cross", label: "Cross +" },
        ],
      },
      { type: "divider" },
      { type: "color", key: "color", label: "Color", default: "#a0a0a0" },
      {
        type: "range",
        key: "opacity",
        label: "Opacity",
        default: 0.4,
        min: 0,
        max: 1,
        step: 0.01,
        displayMultiplier: 100,
        displayStep: 10,
      },
    ];
  }

  render(svg: SVGSVGElement, width: number, height: number, settings: Record<string, any>): void {
    const group = this.getGroup(svg, settings.__instanceId);
    const { step: rawStep, stepUnit, dotSize, shape, color, opacity } = settings;

    const desiredStep = this.resolveUnit(
      rawStep,
      stepUnit || "px",
      Math.min(width, height),
      svg.parentElement,
    );
    const step = this.getSafeStep(width, height, desiredStep, shape);
    if (step < 2) return;

    const patternId = this.getPatternId(settings.__instanceId);
    const defs = this.getDefs(svg);
    defs.querySelector(`#${patternId}`)?.remove();
    const pattern = document.createElementNS(SVG_NS, "pattern");
    pattern.setAttribute("id", patternId);
    pattern.setAttribute("data-string-grid-pattern-for", settings.__instanceId);
    pattern.setAttribute("patternUnits", "userSpaceOnUse");
    pattern.setAttribute("width", String(step));
    pattern.setAttribute("height", String(step));
    pattern.setAttribute("x", String(step / 2));
    pattern.setAttribute("y", String(step / 2));

    const center = step / 2;

    if (shape === "cross") {
      pattern.appendChild(
        this.createLine(
          center - dotSize * 2,
          center,
          center + dotSize * 2,
          center,
          color,
          opacity,
          0.8,
        ),
      );
      pattern.appendChild(
        this.createLine(
          center,
          center - dotSize * 2,
          center,
          center + dotSize * 2,
          color,
          opacity,
          0.8,
        ),
      );
    } else {
      const circle = document.createElementNS(SVG_NS, "circle") as SVGCircleElement;
      circle.setAttribute("cx", String(center));
      circle.setAttribute("cy", String(center));
      circle.setAttribute("r", String(dotSize));
      circle.setAttribute("fill", color);
      circle.setAttribute("fill-opacity", String(opacity));
      pattern.appendChild(circle);
    }

    defs.appendChild(pattern);

    const rect = this.createRect(0, 0, width, height, `url(#${patternId})`, 1);
    rect.setAttribute("fill-opacity", "1");
    group.appendChild(rect);
  }

  override clear(svg: SVGSVGElement, instanceId: string): void {
    super.clear(svg, instanceId);

    const pattern = svg.querySelector(`[data-string-grid-pattern-for="${instanceId}"]`);
    pattern?.remove();
  }

  private getDefs(svg: SVGSVGElement): SVGDefsElement {
    let defs = svg.querySelector("defs") as SVGDefsElement | null;
    if (!defs) {
      defs = document.createElementNS(SVG_NS, "defs") as SVGDefsElement;
      svg.insertBefore(defs, svg.firstChild);
    }
    return defs;
  }

  private getPatternId(instanceId: string): string {
    return `string-grid-dot-pattern-${instanceId.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  }

  private getSafeStep(width: number, height: number, step: number, shape: string): number {
    const minStep = 2;
    const normalizedStep = Math.max(step, minStep);
    const maxCells =
      shape === "cross" ? DotGridAdapter.MAX_CROSS_CELLS : DotGridAdapter.MAX_DOT_CELLS;
    const estimatedCells = Math.floor(width / normalizedStep) * Math.floor(height / normalizedStep);

    if (estimatedCells <= maxCells) {
      return normalizedStep;
    }

    return Math.max(normalizedStep, Math.sqrt((width * height) / maxCells));
  }
}
